import { useEffect, useState, type ReactNode } from "react";
import {
  getColdLoadTimeline,
  type TimelineEntry,
} from "@freightsimple/observability/observability";
import { isColdStartOverlayEnabled } from "./coldStartOverlayState";

/**
 * Dev tool: shows the cold-load phase timeline in the bottom-right of the
 * page so reproductions can be inspected without leaving the browser. Two
 * independent paths surface it:
 *   1. Manual: localStorage flag from a DevMenu row. Always shows, even on
 *      fast loads.
 *   2. Auto: any cold load whose boot.total exceeds the threshold below
 *      (admin-only app — typical loads are ~1s, so a >5s load is unambiguously
 *      "something is wrong" and surfacing the panel captures the data right
 *      when an admin would want it without needing to have remembered to flip
 *      the manual toggle).
 *
 * This is the *engineering* view — a raw phase-timeline dump. Mount it only
 * where engineers should see it. Apps with a mixed audience (Hubble) gate the
 * mount site and show non-engineering users a friendlier slow-boot notice
 * instead; both read the same boot data via `useColdLoadEntries` and share the
 * same `AUTO_SHOW_BOOT_THRESHOLD_MS` so they agree on what counts as "slow".
 *
 * Polls the in-memory ring buffer every 250ms for the first 60s after mount
 * regardless of either path so the auto-show check has data to evaluate.
 * The last snapshot stays on screen until dismissed (per-session).
 */
const POLL_MS = 250;
const POLL_DURATION_MS = 60_000;

/**
 * The boot.total duration over which a cold load is treated as "slow" and the
 * overlay auto-shows. Exported so app-specific slow-boot notices agree with the
 * engineering overlay on what counts as slow.
 */
export const AUTO_SHOW_BOOT_THRESHOLD_MS = 5_000;

/**
 * Polls the in-memory cold-load ring buffer every 250ms for the first 60s
 * after activation and returns the latest snapshot. Shared by the engineering
 * ColdStartOverlay panel and any app-specific slow-boot notice so they observe
 * the same boot timeline. Polling is inert (and prior entries are retained)
 * while `active` is false.
 */
export function useColdLoadEntries(active: boolean): readonly TimelineEntry[] {
  const [entries, setEntries] = useState<readonly TimelineEntry[]>([]);

  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const tick = () => {
      setEntries([...getColdLoadTimeline()]);
    };
    tick();
    const interval = window.setInterval(() => {
      tick();
      if (performance.now() - start > POLL_DURATION_MS) {
        window.clearInterval(interval);
      }
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [active]);

  return entries;
}

export function ColdStartOverlay(): ReactNode {
  const [manuallyEnabled] = useState(() => isColdStartOverlayEnabled());
  const [dismissed, setDismissed] = useState(false);
  const entries = useColdLoadEntries(!dismissed);

  if (dismissed) return null;

  const summary = summarize_(entries);
  // Auto-show only in production. Local Vite dev cold-boots are always several
  // seconds (on-demand module compilation), so the >5s threshold would fire on
  // nearly every reload and the panel would appear even with the manual toggle
  // off. In dev, only the explicit DevMenu toggle surfaces it.
  const autoShown =
    !import.meta.env.DEV &&
    summary.bootTotalMs !== undefined &&
    summary.bootTotalMs > AUTO_SHOW_BOOT_THRESHOLD_MS;
  if (!manuallyEnabled && !autoShown) return null;

  return (
    <ColdStartOverlayPanel_
      entries={entries}
      summary={summary}
      autoShown={autoShown && !manuallyEnabled}
      onDismiss={() => setDismissed(true)}
    />
  );
}

interface PanelProps {
  entries: readonly TimelineEntry[];
  summary: Summary;
  autoShown: boolean;
  onDismiss: () => void;
}

function ColdStartOverlayPanel_({
  entries,
  summary,
  autoShown,
  onDismiss,
}: PanelProps) {
  return (
    <div
      data-testid="cold-start-overlay"
      style={{
        position: "fixed",
        right: 12,
        bottom: 12,
        zIndex: 2147483000,
        maxHeight: "70vh",
        width: 360,
        overflow: "auto",
        background: "rgba(20, 22, 32, 0.92)",
        color: "#e4e7ef",
        font: "12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8,
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <strong style={{ fontSize: 12, letterSpacing: 0.4 }}>
            COLD START
          </strong>
          {autoShown ? (
            <span
              style={{
                fontSize: 10,
                letterSpacing: 0.4,
                padding: "1px 6px",
                borderRadius: 4,
                background: "rgba(248, 113, 113, 0.18)",
                color: "#fca5a5",
              }}
            >
              SLOW
            </span>
          ) : undefined}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss cold start overlay"
          style={{
            background: "transparent",
            color: "#9aa3b2",
            border: "none",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
      <CountersRow_ summary={summary} />
      <div
        style={{
          height: 1,
          background: "rgba(255,255,255,0.08)",
          margin: "6px 0",
        }}
      />
      <Timeline_ entries={entries} />
    </div>
  );
}

interface Summary {
  silentAttempts: number;
  silentSuccess: number;
  silentRedirected: number;
  silentShortCircuit: number;
  silentRedirectFailed: number;
  redirectInitiated: number;
  redirectInProgressThrown: number;
  authInitMs: number | undefined;
  msalInitializeMs: number | undefined;
  bootTotalMs: number | undefined;
  firstRenderMs: number | undefined;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
function summarize_(entries: readonly TimelineEntry[]): Summary {
  const summary: Summary = {
    silentAttempts: 0,
    silentSuccess: 0,
    silentRedirected: 0,
    silentShortCircuit: 0,
    silentRedirectFailed: 0,
    redirectInitiated: 0,
    redirectInProgressThrown: 0,
    authInitMs: undefined,
    msalInitializeMs: undefined,
    bootTotalMs: undefined,
    firstRenderMs: undefined,
  };
  const startsByName = new Map<string, number>();
  for (const e of entries) {
    if (e.name === "auth.silent_token.start") summary.silentAttempts++;
    if (e.name === "auth.silent_token.end") {
      const result = e.attrs?.["result"];
      if (result === "success") summary.silentSuccess++;
      if (result === "redirected") summary.silentRedirected++;
      if (result === "short_circuit") summary.silentShortCircuit++;
      if (result === "redirect_failed") summary.silentRedirectFailed++;
    }
    if (e.name === "auth.redirect_initiated") summary.redirectInitiated++;
    if (e.name === "auth.redirect_in_progress_thrown")
      summary.redirectInProgressThrown++;
    if (e.name === "boot.first_render") summary.firstRenderMs = e.t;

    if (e.name.endsWith(".start")) {
      const base = e.name.slice(0, -".start".length);
      startsByName.set(base, e.t);
    }
    if (e.name.endsWith(".end")) {
      const base = e.name.slice(0, -".end".length);
      const startT = startsByName.get(base);
      if (startT !== undefined) {
        const dur = e.t - startT;
        if (base === "auth.init") summary.authInitMs = dur;
        if (base === "auth.msal_initialize") summary.msalInitializeMs = dur;
        if (base === "boot.total") summary.bootTotalMs = dur;
      }
    }
  }
  return summary;
}

function CountersRow_({ summary }: { summary: Summary }) {
  const counters: Array<[string, string]> = [
    ["msal.initialize", fmtMs_(summary.msalInitializeMs)],
    ["auth.init", fmtMs_(summary.authInitMs)],
    ["boot.total", fmtMs_(summary.bootTotalMs)],
    ["first_render", fmtMs_(summary.firstRenderMs)],
    [
      "silent_token",
      `${summary.silentSuccess}✓ / ${summary.silentRedirected + summary.silentRedirectFailed}↻ / ${summary.silentShortCircuit}∅ (${summary.silentAttempts} total)`,
    ],
    ["redirect_initiated", String(summary.redirectInitiated)],
    ["short_circuit_thrown", String(summary.redirectInProgressThrown)],
  ];
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 2 }}
    >
      {counters.map(([k, v]) => (
        <KV_ key={k} label={k} value={v} />
      ))}
    </div>
  );
}

function KV_({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span style={{ color: "#9aa3b2" }}>{label}</span>
      <span style={{ color: "#e4e7ef", textAlign: "right" }}>{value}</span>
    </>
  );
}

function Timeline_({ entries }: { entries: readonly TimelineEntry[] }) {
  if (entries.length === 0) {
    return (
      <div style={{ color: "#9aa3b2" }}>(no phase events recorded yet)</div>
    );
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: 8,
        rowGap: 1,
      }}
    >
      {entries.map((e) => (
        <TimelineRow_ key={`${e.name}@${e.t}`} entry={e} />
      ))}
    </div>
  );
}

function TimelineRow_({ entry }: { entry: TimelineEntry }) {
  const color = colorFor_(entry.name);
  return (
    <>
      <span style={{ color: "#9aa3b2", fontVariantNumeric: "tabular-nums" }}>
        {fmtMs_(entry.t)}
      </span>
      <span style={{ color }}>
        {entry.name}
        {entry.attrs ? ` ${fmtAttrs_(entry.attrs)}` : ""}
      </span>
    </>
  );
}

function fmtMs_(ms: number | undefined): string {
  if (ms === undefined) return "—";
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function fmtAttrs_(attrs: Record<string, string | number | boolean>): string {
  return Object.entries(attrs)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(" ");
}

function colorFor_(name: string): string {
  if (name.startsWith("auth.redirect")) return "#fbbf24";
  if (name.includes("error")) return "#f87171";
  if (name.startsWith("auth.silent_token.end")) return "#86efac";
  if (name.startsWith("auth.")) return "#a5b4fc";
  if (name.startsWith("boot.")) return "#7dd3fc";
  return "#e4e7ef";
}
